import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { MetaTags } from "@/components/seo/MetaTags";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Loader2, Trash2, Plus, ShieldAlert, Cpu, Activity, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api-client";

export default function ServicesRegistry() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newServiceName, setNewServiceName] = useState("");

  const { data: profile } = useQuery({
    queryKey: ["profile"],
    queryFn: () => api.getProfile(),
  });
  const isPro = profile?.is_pro || false;

  const { data: services, isLoading } = useQuery({
    queryKey: ["services"],
    queryFn: () => api.getServices()
  });

  const createServiceMutation = useMutation({
    mutationFn: (name: string) => api.createService(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["services"] });
      setIsAddModalOpen(false);
      setNewServiceName("");
      toast.success("Service created successfully!");
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to create service");
    }
  });

  const deleteServiceMutation = useMutation({
    mutationFn: (id: string) => api.deleteService(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["services"] });
      toast.success("Service deleted.");
    }
  });

  const handleCreateService = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newServiceName.trim()) return;

    if (!isPro && services && services.length >= 1) {
      toast.error("Free accounts are limited to 1 Service mapping boundary. Upgrade to Pro to connect unlimited stack sources.");
      navigate("/pricing");
      return;
    }

    createServiceMutation.mutate(newServiceName.trim());
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <MetaTags
        title="Service Dashboard"
        description="Monitor status, connected integrations, and AI health assessments for your services."
        canonical="https://uptimewing.com/services"
      />

      <div className="flex items-center justify-between border-b-2 border-black pb-4 select-none">
        <div className="flex items-center gap-2">
          <Link
            to="/dashboard"
            className="flex items-center justify-center h-8 w-8 rounded-none border-2 border-black bg-background hover:bg-accent transition-all"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <h1 className="text-xl font-black uppercase tracking-tight leading-none">Services</h1>
        </div>
        <Button
          onClick={() => setIsAddModalOpen(true)}
          className="h-10 rounded-none border-2 border-black bg-black text-white hover:bg-white hover:text-black font-black uppercase text-xs"
        >
          [+ Add Service]
        </Button>
      </div>

      <div className="space-y-6">
        {(!services || services.length === 0) && (
          <div className="border-4 border-black p-8 text-center bg-white space-y-4">
            <ShieldAlert className="w-12 h-12 mx-auto text-zinc-400" />
            <h3 className="text-sm font-black uppercase">No Active Services</h3>
            <p className="font-mono text-xs text-zinc-500 uppercase">
              Add a service boundary to correlate Sentry exception events, Datadog metric breaches, and URL checkpoints.
            </p>
          </div>
        )}

        {services?.map((service: any) => {
          // Status fallback logic
          const isServiceUp = service.status === "active";
          return (
            <div 
              key={service.id} 
              className="border-4 border-black p-6 bg-background rounded-none space-y-4 shadow-[4px_4px_0_0_#000] cursor-pointer hover:-translate-x-0.5 hover:-translate-y-0.5 transition-all"
              onClick={() => navigate(`/services/${service.id}`)}
            >
              <div className="flex items-center justify-between border-b-2 border-black pb-3">
                <div className="flex items-center gap-2.5">
                  <span className={`h-3 w-3 rounded-none border border-black ${isServiceUp ? "bg-status-up animate-pulse" : "bg-status-down"}`} />
                  <h3 className="text-md font-black uppercase tracking-wider">{service.name}</h3>
                </div>
                <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                  <button
                    onClick={() => {
                      const serviceId = service.id;
                      const serviceName = service.name;
                      toast(`Delete service boundary "${serviceName}"?`, {
                        action: {
                          label: "Confirm Deletion",
                          onClick: () => deleteServiceMutation.mutate(serviceId),
                        },
                      });
                    }}
                    className="text-red-500 hover:text-red-700 p-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 font-mono text-[11px] uppercase">
                <div className="border-2 border-black p-3 bg-zinc-50 space-y-1">
                  <div className="text-zinc-400 font-black">Status Summary</div>
                  <div className="font-black text-black">Uptime: 99.98%</div>
                  <div className="text-[10px] text-zinc-500">Last incident: 2 days ago</div>
                </div>

                <div className="border-2 border-black p-3 bg-zinc-50 space-y-1">
                  <div className="text-zinc-400 font-black">Linked Context Sources</div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    <span className="bg-purple-100 text-purple-800 text-[9px] px-1 border border-purple-300 font-black">SENTRY</span>
                    <span className="bg-orange-100 text-orange-800 text-[9px] px-1 border border-orange-300 font-black">GITHUB</span>
                    <span className="bg-blue-100 text-blue-800 text-[9px] px-1 border border-blue-300 font-black">DATADOG</span>
                  </div>
                </div>

                <div className="border-2 border-black p-3 bg-zinc-50 space-y-1">
                  <div className="text-zinc-400 font-black">Diagnosis Count</div>
                  <div className="font-black text-black">2 Analysed / month</div>
                  <div className="text-[9px] text-zinc-500">Quota: {isPro ? "50/mo" : "5/mo"}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* CREATE SERVICE MODAL */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 backdrop-blur-[1px] p-4 select-none">
          <div className="bg-white border-4 border-black p-8 rounded-none w-full max-w-md space-y-6 text-black font-mono">
            <div className="flex items-center justify-between border-b-2 border-black pb-3">
              <h2 className="text-lg font-black uppercase tracking-tight">Create Service Boundary</h2>
              <button onClick={() => setIsAddModalOpen(false)} className="font-black hover:text-red-500">[x]</button>
            </div>
            
            <form onSubmit={handleCreateService} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-[9px] font-black uppercase text-zinc-400">Service Name</label>
                <Input
                  required
                  placeholder="e.g. api-prod"
                  value={newServiceName}
                  onChange={e => setNewServiceName(e.target.value)}
                  className="rounded-none border-2 border-black text-xs h-10 bg-white"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-11 rounded-none border-2 border-black bg-black text-white hover:bg-white hover:text-black font-black uppercase text-xs tracking-wider shadow-[3px_3px_0_0_#000]"
              >
                Create Service
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
